/**
 * Platform Analytics API client.
 *
 * Helix-only endpoints for user engagement, feature stickiness,
 * company activity, and platform health metrics.
 */
import api from './client';

export interface PlatformOverview {
  period: { date_from: string; date_to: string };
  kpis: {
    dau: number;
    wau: number;
    mau: number;
    total_requests: number;
    active_companies: number;
    active_supplier_companies: number;
    active_customer_companies: number;
    stickiness_dau_mau: number;
    stickiness_wau_mau: number;
  };
}

export interface FeatureUsageItem {
  feature: string;
  requests: number;
  unique_users: number;
  unique_companies: number;
  percentage: number;
}

export interface CompanyEngagement {
  company_id: number;
  company_name: string;
  company_type: string;
  active_users: number;
  total_requests: number;
  unique_features: number;
  engagement_score: number;
  engagement_level?: 'strong' | 'moderate' | 'low' | 'inactive';
}

export interface UserActivityItem {
  user_id: number;
  email: string;
  name: string;
  company: string;
  company_type: string;
  total_requests: number;
  unique_features: number;
  active_days: number;
}

export interface PlatformTrendPoint {
  date: string;
  requests: number;
  unique_users: number;
  unique_companies: number;
}

export interface ProcessAdoptionItem {
  feature: string;
  companies_using: number;
  total_companies: number;
  adoption_percentage: number;
  total_requests: number;
  status: 'high' | 'medium' | 'low';
}

interface DateRangeParams {
  date_from?: string;
  date_to?: string;
  company_type?: string;
}

export const platformAnalyticsAPI = {
  getOverview: (params?: DateRangeParams) =>
    api.get<PlatformOverview>('/analytics/platform/overview/', { params }),

  getFeatureUsage: (params?: DateRangeParams) =>
    api.get<{ features: FeatureUsageItem[]; total_requests: number }>(
      '/analytics/platform/feature-usage/', { params }
    ),

  getCompanyEngagement: (params?: DateRangeParams & { limit?: number }) =>
    api.get<{ companies: CompanyEngagement[] }>(
      '/analytics/platform/company-engagement/', { params }
    ),

  getUserActivity: (params?: DateRangeParams & { company_id?: number; limit?: number }) =>
    api.get<{ users: UserActivityItem[] }>(
      '/analytics/platform/user-activity/', { params }
    ),

  getActivityTrend: (params?: DateRangeParams) =>
    api.get<{ trend: PlatformTrendPoint[] }>(
      '/analytics/platform/activity-trend/', { params }
    ),

  getProcessAdoption: (params?: DateRangeParams) =>
    api.get<{ adoption: ProcessAdoptionItem[]; total_active_companies: number }>(
      '/analytics/platform/process-adoption/', { params }
    ),
};
